import React, { useState } from "react";
import {
    FaBook,
    FaSearch,
    FaPlus,
    FaEdit,
    FaTrash,
    FaEye,
    FaDownload,
    FaUpload,
    FaFilter,
    FaSort,
    FaCalendarAlt,
    FaUser,
    FaTag,
    FaStar,
    FaChartBar,
    FaCopy,
    FaShareAlt,
    FaLock,
    FaGlobe,
    FaBars,
    FaTimes,
    FaChevronDown,
    FaChevronUp
} from "react-icons/fa";

const SuperAdminKnowledgeBaseManager = () => {
    const [articles, setArticles] = useState([
        {
            id: 1,
            title: "How to Process Refunds",
            content: "Step-by-step guide for processing customer refunds through the admin panel.",
            category: "Payment",
            subcategory: "Refunds",
            status: "published",
            views: 1245,
            helpful: 89,
            notHelpful: 12,
            author: "Admin User",
            created: "2024-01-10",
            updated: "2024-01-15",
            tags: ["refund", "payment", "customer", "process"],
            visibility: "public",
            featured: true,
            attachments: ["refund_guide.pdf", "screenshots.zip"]
        },
        {
            id: 2,
            title: "Delivery Issues Troubleshooting",
            content: "Common delivery problems and their solutions for customer support agents.",
            category: "Delivery",
            subcategory: "Troubleshooting",
            status: "published",
            views: 892,
            helpful: 67,
            notHelpful: 8,
            author: "Agent Smith",
            created: "2024-01-05",
            updated: "2024-01-12",
            tags: ["delivery", "troubleshooting", "late", "missing"],
            visibility: "public",
            featured: true,
            attachments: ["delivery_flow.png"]
        },
        {
            id: 3,
            title: "App Crash Solutions",
            content: "Technical solutions for common mobile app crashes reported by users.",
            category: "Technical",
            subcategory: "Mobile App",
            status: "draft",
            views: 567,
            helpful: 45,
            notHelpful: 15,
            author: "Tech Support",
            created: "2024-01-08",
            updated: "2024-01-14",
            tags: ["app", "crash", "technical", "mobile"],
            visibility: "private",
            featured: false,
            attachments: []
        },
        {
            id: 4,
            title: "New Payment Methods Guide",
            content: "Guide for supporting new payment methods added to the platform.",
            category: "Payment",
            subcategory: "Methods",
            status: "published",
            views: 342,
            helpful: 28,
            notHelpful: 3,
            author: "Payment Team",
            created: "2024-01-12",
            updated: "2024-01-15",
            tags: ["payment", "methods", "guide", "new"],
            visibility: "public",
            featured: false,
            attachments: ["payment_methods.docx"]
        },
        {
            id: 5,
            title: "Customer Account Management",
            content: "Procedures for managing customer accounts, including verification and updates.",
            category: "Account",
            subcategory: "Management",
            status: "archived",
            views: 1567,
            helpful: 123,
            notHelpful: 25,
            author: "Admin User",
            created: "2023-12-20",
            updated: "2024-01-10",
            tags: ["account", "management", "customer", "verification"],
            visibility: "public",
            featured: true,
            attachments: []
        }
    ]);

    const [categories, setCategories] = useState([
        { id: 1, name: "Payment", count: 24, subcategories: ["Refunds", "Methods", "Failed Payments", "Charges"] },
        { id: 2, name: "Delivery", count: 18, subcategories: ["Tracking", "Late", "Missing", "Address"] },
        { id: 3, name: "Order", count: 15, subcategories: ["Placement", "Modification", "Cancellation", "Tracking"] },
        { id: 4, name: "Technical", count: 12, subcategories: ["App", "Website", "Account", "Payment"] },
        { id: 5, name: "Account", count: 8, subcategories: ["Login", "Profile", "Security", "Delete"] }
    ]);

    const [analytics, setAnalytics] = useState({
        totalArticles: 45,
        published: 32,
        drafts: 8,
        archived: 5,
        totalViews: 15678,
        avgRating: 4.6,
        helpfulRate: "89%",
        searchQueries: ["refund process", "delivery time", "app login"]
    });

    const [filters, setFilters] = useState({
        category: "all",
        status: "all",
        visibility: "all",
        featured: "all",
        search: ""
    });

    const [sortBy, setSortBy] = useState("updated");
    const [sortOrder, setSortOrder] = useState("desc");
    const [selectedArticles, setSelectedArticles] = useState([]);
    const [showNewArticle, setShowNewArticle] = useState(false);
    const [editingArticle, setEditingArticle] = useState(null);
    const [newArticle, setNewArticle] = useState({
        title: "",
        content: "",
        category: "",
        subcategory: "",
        tags: [],
        visibility: "public",
        featured: false
    });

    // Mobile-specific states
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [expandedArticle, setExpandedArticle] = useState(null);

    const handleFilterChange = (filterName, value) => {
        setFilters({ ...filters, [filterName]: value });
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("desc");
        }
    };

    const handleSelectArticle = (articleId) => {
        if (selectedArticles.includes(articleId)) {
            setSelectedArticles(selectedArticles.filter(id => id !== articleId));
        } else {
            setSelectedArticles([...selectedArticles, articleId]);
        }
    };

    const handleSelectAll = () => {
        if (selectedArticles.length === filteredArticles.length) {
            setSelectedArticles([]);
        } else {
            setSelectedArticles(filteredArticles.map(a => a.id));
        }
    };

    const handleBulkAction = (action) => {
        switch (action) {
            case 'publish':
                setArticles(articles.map(article =>
                    selectedArticles.includes(article.id) ? { ...article, status: "published" } : article
                ));
                break;
            case 'archive':
                setArticles(articles.map(article =>
                    selectedArticles.includes(article.id) ? { ...article, status: "archived" } : article
                ));
                break;
            case 'delete':
                if (window.confirm(`Delete ${selectedArticles.length} articles?`)) {
                    setArticles(articles.filter(article => !selectedArticles.includes(article.id)));
                    setSelectedArticles([]);
                }
                break;
            case 'feature':
                setArticles(articles.map(article =>
                    selectedArticles.includes(article.id) ? { ...article, featured: true } : article
                ));
                break;
        }
    };

    const handleSaveArticle = () => {
        if (editingArticle) {
            // Update existing article
            setArticles(articles.map(article =>
                article.id === editingArticle.id
                    ? { ...article, ...newArticle, updated: new Date().toISOString().split('T')[0] }
                    : article
            ));
        } else {
            // Add new article
            const newId = Math.max(...articles.map(a => a.id)) + 1;
            const articleToAdd = {
                id: newId,
                ...newArticle,
                status: "draft",
                views: 0,
                helpful: 0,
                notHelpful: 0,
                author: "SuperAdmin",
                created: new Date().toISOString().split('T')[0],
                updated: new Date().toISOString().split('T')[0],
                attachments: []
            };
            setArticles([articleToAdd, ...articles]);
        }
        setShowNewArticle(false);
        setEditingArticle(null);
        setNewArticle({
            title: "",
            content: "",
            category: "",
            subcategory: "",
            tags: [],
            visibility: "public",
            featured: false
        });
    };

    const handleEditArticle = (article) => {
        setEditingArticle(article);
        setNewArticle({
            title: article.title,
            content: article.content,
            category: article.category,
            subcategory: article.subcategory,
            tags: [...article.tags],
            visibility: article.visibility,
            featured: article.featured
        });
        setShowNewArticle(true);
    };

    const handleDeleteArticle = (articleId) => {
        if (window.confirm("Are you sure you want to delete this article?")) {
            setArticles(articles.filter(article => article.id !== articleId));
        }
    };

    const handleDuplicateArticle = (article) => {
        const newId = Math.max(...articles.map(a => a.id)) + 1;
        const duplicatedArticle = {
            ...article,
            id: newId,
            title: `${article.title} (Copy)`,
            views: 0,
            helpful: 0,
            notHelpful: 0,
            created: new Date().toISOString().split('T')[0],
            updated: new Date().toISOString().split('T')[0],
            status: "draft"
        };
        setArticles([duplicatedArticle, ...articles]);
    };

    const filteredArticles = articles
        .filter(article => {
            const matchesCategory = filters.category === "all" || article.category === filters.category;
            const matchesStatus = filters.status === "all" || article.status === filters.status;
            const matchesVisibility = filters.visibility === "all" || article.visibility === filters.visibility;
            const matchesFeatured = filters.featured === "all" ||
                (filters.featured === "yes" && article.featured) ||
                (filters.featured === "no" && !article.featured);
            const matchesSearch = filters.search === "" ||
                article.title.toLowerCase().includes(filters.search.toLowerCase()) ||
                article.content.toLowerCase().includes(filters.search.toLowerCase()) ||
                article.tags.some(tag => tag.toLowerCase().includes(filters.search.toLowerCase()));

            return matchesCategory && matchesStatus && matchesVisibility && matchesFeatured && matchesSearch;
        })
        .sort((a, b) => {
            const order = sortOrder === "asc" ? 1 : -1;
            switch (sortBy) {
                case "title":
                    return order * a.title.localeCompare(b.title);
                case "views":
                    return order * (a.views - b.views);
                case "updated":
                    return order * new Date(a.updated) - new Date(b.updated);
                case "helpful":
                    return order * (a.helpful - b.helpful);
                default:
                    return 0;
            }
        });

    const getStatusColor = (status) => {
        switch (status) {
            case 'published': return '#28a745';
            case 'draft': return '#ffc107';
            case 'archived': return '#6c757d';
            default: return '#6c757d';
        }
    };

    const getVisibilityIcon = (visibility) => {
        switch (visibility) {
            case 'public': return <FaGlobe style={{ color: "#28a745" }} />;
            case 'private': return <FaLock style={{ color: "#dc3545" }} />;
            default: return <FaGlobe />;
        }
    };

    const styles = {
        container: {
            padding: "12px",
            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
            backgroundColor: "#f5f7fa",
            minHeight: "100vh"
        },
        mobileHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            paddingBottom: "15px",
            borderBottom: "1px solid #e0e0e0",
            position: "sticky",
            top: 0,
            backgroundColor: "#f5f7fa",
            zIndex: 100,
            paddingTop: "10px"
        },
        hamburgerButton: {
            backgroundColor: "transparent",
            border: "none",
            fontSize: "20px",
            color: "#6a0dad",
            padding: "8px",
            borderRadius: "5px",
            cursor: "pointer"
        },
        title: {
            fontSize: "20px",
            fontWeight: "700",
            color: "#2c3e50",
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "8px"
        },
        mobileControls: {
            display: "flex",
            gap: "10px",
            alignItems: "center"
        },
        mobileActionButton: {
            padding: "8px 12px",
            backgroundColor: "#6a0dad",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "12px",
            fontWeight: "500"
        },
        mobileMenu: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column"
        },
        mobileMenuContent: {
            backgroundColor: "#fff",
            width: "85%",
            height: "100%",
            padding: "20px",
            overflowY: "auto"
        },
        mobileMenuHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
            paddingBottom: "15px",
            borderBottom: "1px solid #e0e0e0"
        },
        mobileMenuTitle: {
            fontSize: "18px",
            fontWeight: "600",
            color: "#2c3e50"
        },
        mobileMenuClose: {
            backgroundColor: "transparent",
            border: "none",
            fontSize: "20px",
            color: "#6c757d",
            cursor: "pointer"
        },
        searchContainer: {
            position: "relative",
            width: "100%",
            marginBottom: "15px"
        },
        searchInput: {
            width: "100%",
            padding: "12px 40px 12px 15px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            fontSize: "14px",
            outline: "none",
            backgroundColor: "#fff"
        },
        searchIcon: {
            position: "absolute",
            right: "15px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#6c757d"
        },
        analyticsBar: {
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "12px",
            marginBottom: "20px"
        },
        analyticCard: {
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "15px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            textAlign: "center"
        },
        analyticValue: {
            fontSize: "24px",
            fontWeight: "bold",
            color: "#2c3e50",
            marginBottom: "5px"
        },
        analyticLabel: {
            fontSize: "12px",
            color: "#6c757d",
            fontWeight: "500"
        },
        filterButton: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 15px",
            backgroundColor: "#fff",
            border: "1px solid #ddd",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "500",
            color: "#2c3e50",
            cursor: "pointer",
            width: "100%",
            justifyContent: "center",
            marginBottom: "15px"
        },
        filterPanel: {
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "20px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            display: showFilterPanel ? "block" : "none"
        },
        filterGroup: {
            marginBottom: "15px"
        },
        filterLabel: {
            fontSize: "12px",
            color: "#6c757d",
            fontWeight: "500",
            marginBottom: "5px",
            display: "block"
        },
        filterSelect: {
            width: "100%",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            fontSize: "14px",
            backgroundColor: "#fff"
        },
        sortButtons: {
            display: "flex",
            gap: "8px",
            overflowX: "auto",
            marginBottom: "15px",
            paddingBottom: "5px"
        },
        sortButton: {
            padding: "8px 12px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            backgroundColor: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "12px",
            whiteSpace: "nowrap"
        },
        sortButtonActive: {
            backgroundColor: "#6a0dad",
            color: "white",
            borderColor: "#6a0dad"
        },
        bulkActions: {
            backgroundColor: "#f8f9fa",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "15px",
            display: selectedArticles.length > 0 ? "flex" : "none",
            flexDirection: "column",
            gap: "10px"
        },
        bulkActionsRow: {
            display: "flex",
            gap: "10px",
            overflowX: "auto",
            paddingBottom: "5px"
        },
        bulkActionButton: {
            padding: "8px 12px",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            whiteSpace: "nowrap"
        },
        articlesList: {
            display: "flex",
            flexDirection: "column",
            gap: "15px"
        },
        articleCard: {
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "15px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        },
        articleCardFeatured: {
            border: "2px solid #ffc107"
        },
        articleHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "10px"
        },
        articleTitleRow: {
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            flex: 1
        },
        articleTitle: {
            fontSize: "16px",
            fontWeight: "600",
            color: "#2c3e50",
            margin: 0,
            flex: 1
        },
        articleMeta: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "10px",
            fontSize: "11px",
            color: "#6c757d",
            flexWrap: "wrap"
        },
        articleContent: {
            fontSize: "13px",
            color: "#495057",
            lineHeight: "1.4",
            marginBottom: "10px"
        },
        tagContainer: {
            display: "flex",
            flexWrap: "wrap",
            gap: "5px",
            marginBottom: "10px"
        },
        tag: {
            padding: "3px 8px",
            backgroundColor: "#e9ecef",
            color: "#495057",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "500"
        },
        statusBadge: {
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "600",
            display: "inline-block"
        },
        statsContainer: {
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            marginBottom: "10px"
        },
        statItem: {
            textAlign: "center",
            padding: "10px",
            backgroundColor: "#f8f9fa",
            borderRadius: "6px"
        },
        statNumber: {
            fontSize: "14px",
            fontWeight: "700",
            color: "#2c3e50",
            marginBottom: "2px"
        },
        statLabel: {
            fontSize: "10px",
            color: "#6c757d",
            textTransform: "uppercase"
        },
        articleFooter: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: "10px",
            borderTop: "1px solid #e9ecef"
        },
        actionButtons: {
            display: "flex",
            gap: "6px"
        },
        smallButton: {
            padding: "6px 10px",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            minWidth: "40px",
            justifyContent: "center"
        },
        expandButton: {
            backgroundColor: "transparent",
            border: "none",
            color: "#6a0dad",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "12px",
            fontWeight: "500",
            padding: "5px 0"
        },
        modalOverlay: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "15px"
        },
        modalContent: {
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "20px",
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto"
        },
        modalHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px"
        },
        modalTitle: {
            fontSize: "18px",
            fontWeight: "600",
            color: "#2c3e50",
            margin: 0
        },
        formGroup: {
            marginBottom: "15px"
        },
        formLabel: {
            display: "block",
            marginBottom: "6px",
            fontWeight: "500",
            color: "#495057",
            fontSize: "13px"
        },
        formInput: {
            width: "100%",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            fontSize: "14px"
        },
        formTextarea: {
            width: "100%",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            fontSize: "14px",
            resize: "vertical",
            minHeight: "120px"
        },
        tagInputContainer: {
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            minHeight: "45px",
            alignItems: "center"
        },
        tagInput: {
            border: "none",
            outline: "none",
            flex: 1,
            minWidth: "100px",
            fontSize: "14px"
        },
        categoryList: {
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "20px"
        },
        categoryItem: {
            padding: "12px",
            borderRadius: "8px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #e0e0e0"
        },
        categoryItemActive: {
            backgroundColor: "#6a0dad",
            color: "white",
            borderColor: "#6a0dad"
        },
        categoryCount: {
            fontSize: "12px",
            backgroundColor: "rgba(255,255,255,0.2)",
            padding: "3px 8px",
            borderRadius: "12px"
        },
        mobileStats: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 0",
            borderBottom: "1px solid #e0e0e0"
        },
        mobileStatItem: {
            textAlign: "center",
            flex: 1
        },
        mobileStatValue: {
            fontSize: "16px",
            fontWeight: "bold",
            color: "#2c3e50",
            display: "block"
        },
        mobileStatLabel: {
            fontSize: "10px",
            color: "#6c757d",
            display: "block"
        }
    };

    return (
        <div style={styles.container}>
            {/* Mobile Header */}
            <div style={styles.mobileHeader}>
                <button 
                    style={styles.hamburgerButton}
                    onClick={() => setShowMobileMenu(true)}
                >
                    <FaBars />
                </button>
                
                <h1 style={styles.title}>
                    <FaBook /> KB Manager
                </h1>
                
                <div style={styles.mobileControls}>
                    <button 
                        style={styles.mobileActionButton}
                        onClick={() => setShowNewArticle(true)}
                    >
                        <FaPlus />
                    </button>
                </div>
            </div>

            {/* Search Bar */}
            <div style={styles.searchContainer}>
                <input
                    type="text"
                    placeholder="Search articles..."
                    style={styles.searchInput}
                    value={filters.search}
                    onChange={(e) => handleFilterChange("search", e.target.value)}
                />
                <div style={styles.searchIcon}>
                    <FaSearch />
                </div>
            </div>

            {/* Mobile Stats Summary */}
            <div style={styles.mobileStats}>
                <div style={styles.mobileStatItem}>
                    <span style={styles.mobileStatValue}>{analytics.totalArticles}</span>
                    <span style={styles.mobileStatLabel}>Articles</span>
                </div>
                <div style={styles.mobileStatItem}>
                    <span style={styles.mobileStatValue}>{analytics.published}</span>
                    <span style={styles.mobileStatLabel}>Published</span>
                </div>
                <div style={styles.mobileStatItem}>
                    <span style={styles.mobileStatValue}>{analytics.totalViews}</span>
                    <span style={styles.mobileStatLabel}>Views</span>
                </div>
            </div>

            {/* Filter Button */}
            <button 
                style={styles.filterButton}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
            >
                <FaFilter /> Filters & Sort
                {showFilterPanel ? <FaChevronUp /> : <FaChevronDown />}
            </button>

            {/* Filter Panel */}
            {showFilterPanel && (
                <div style={styles.filterPanel}>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Category</label>
                        <select
                            style={styles.filterSelect}
                            value={filters.category}
                            onChange={(e) => handleFilterChange("category", e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.name}>{category.name}</option>
                            ))}
                        </select>
                    </div>
                    
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Status</label>
                        <select
                            style={styles.filterSelect}
                            value={filters.status}
                            onChange={(e) => handleFilterChange("status", e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="published">Published</option>
                            <option value="draft">Draft</option>
                            <option value="archived">Archived</option>
                        </select>
                    </div>
                    
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Visibility</label>
                        <select
                            style={styles.filterSelect}
                            value={filters.visibility}
                            onChange={(e) => handleFilterChange("visibility", e.target.value)}
                        >
                            <option value="all">All Visibility</option>
                            <option value="public">Public</option>
                            <option value="private">Private</option>
                        </select>
                    </div>
                    
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Featured</label>
                        <select
                            style={styles.filterSelect}
                            value={filters.featured}
                            onChange={(e) => handleFilterChange("featured", e.target.value)}
                        >
                            <option value="all">All</option>
                            <option value="yes">Featured Only</option>
                            <option value="no">Not Featured</option>
                        </select>
                    </div>
                    
                    <div style={{ marginTop: "15px" }}>
                        <label style={styles.filterLabel}>Sort By</label>
                        <div style={styles.sortButtons}>
                            {['title', 'views', 'updated', 'helpful'].map(field => (
                                <button
                                    key={field}
                                    style={{
                                        ...styles.sortButton,
                                        ...(sortBy === field ? styles.sortButtonActive : {})
                                    }}
                                    onClick={() => handleSort(field)}
                                >
                                    {field.charAt(0).toUpperCase() + field.slice(1)}
                                    {sortBy === field && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Actions */}
            {selectedArticles.length > 0 && (
                <div style={styles.bulkActions}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                        <input
                            type="checkbox"
                            checked={selectedArticles.length === filteredArticles.length}
                            onChange={handleSelectAll}
                            style={{ width: "18px", height: "18px" }}
                        />
                        <span style={{ fontWeight: "600", color: "#2c3e50", fontSize: "14px" }}>
                            {selectedArticles.length} selected
                        </span>
                    </div>
                    <div style={styles.bulkActionsRow}>
                        <button
                            style={{ ...styles.bulkActionButton, backgroundColor: "#28a745", color: "white" }}
                            onClick={() => handleBulkAction('publish')}
                        >
                            Publish
                        </button>
                        <button
                            style={{ ...styles.bulkActionButton, backgroundColor: "#6c757d", color: "white" }}
                            onClick={() => handleBulkAction('archive')}
                        >
                            Archive
                        </button>
                        <button
                            style={{ ...styles.bulkActionButton, backgroundColor: "#ffc107", color: "white" }}
                            onClick={() => handleBulkAction('feature')}
                        >
                            Feature
                        </button>
                        <button
                            style={{ ...styles.bulkActionButton, backgroundColor: "#dc3545", color: "white" }}
                            onClick={() => handleBulkAction('delete')}
                        >
                            Delete
                        </button>
                    </div>
                    <button
                        style={{ ...styles.bulkActionButton, backgroundColor: "transparent", color: "#6c757d", border: "1px solid #ddd", alignSelf: "flex-start" }}
                        onClick={() => setSelectedArticles([])}
                    >
                        Clear Selection
                    </button>
                </div>
            )}

            {/* Articles List */}
            <div style={styles.articlesList}>
                {filteredArticles.length > 0 ? (
                    filteredArticles.map(article => (
                        <div
                            key={article.id}
                            style={{
                                ...styles.articleCard,
                                ...(article.featured ? styles.articleCardFeatured : {})
                            }}
                        >
                            <div style={styles.articleHeader}>
                                <div style={styles.articleTitleRow}>
                                    <input
                                        type="checkbox"
                                        checked={selectedArticles.includes(article.id)}
                                        onChange={() => handleSelectArticle(article.id)}
                                        style={{ marginTop: "3px", width: "16px", height: "16px" }}
                                    />
                                    <h3 style={styles.articleTitle}>{article.title}</h3>
                                </div>
                                <div>
                                    <span style={{
                                        ...styles.statusBadge,
                                        backgroundColor: getStatusColor(article.status) + "20",
                                        color: getStatusColor(article.status)
                                    }}>
                                        {article.status.charAt(0).toUpperCase() + article.status.slice(1)}
                                    </span>
                                </div>
                            </div>

                            <div style={styles.articleMeta}>
                                <span><FaUser size={10} /> {article.author}</span>
                                <span><FaCalendarAlt size={10} /> {article.updated}</span>
                                <span>{getVisibilityIcon(article.visibility)} {article.visibility}</span>
                                {article.featured && <span><FaStar size={10} style={{ color: "#ffc107" }} /> Featured</span>}
                            </div>

                            <div style={styles.tagContainer}>
                                {article.tags.slice(0, 3).map((tag, index) => (
                                    <span key={index} style={styles.tag}>#{tag}</span>
                                ))}
                                {article.tags.length > 3 && (
                                    <span style={styles.tag}>+{article.tags.length - 3}</span>
                                )}
                            </div>

                            <div style={styles.articleContent}>
                                {expandedArticle === article.id ? article.content : `${article.content.substring(0, 100)}...`}
                            </div>

                            {expandedArticle !== article.id && (
                                <button
                                    style={styles.expandButton}
                                    onClick={() => setExpandedArticle(article.id)}
                                >
                                    Read more <FaChevronDown />
                                </button>
                            )}

                            {expandedArticle === article.id && (
                                <button
                                    style={styles.expandButton}
                                    onClick={() => setExpandedArticle(null)}
                                >
                                    Show less <FaChevronUp />
                                </button>
                            )}

                            <div style={styles.statsContainer}>
                                <div style={styles.statItem}>
                                    <div style={styles.statNumber}>{article.views}</div>
                                    <div style={styles.statLabel}>Views</div>
                                </div>
                                <div style={styles.statItem}>
                                    <div style={styles.statNumber}>
                                        {article.helpful}/{article.helpful + article.notHelpful}
                                    </div>
                                    <div style={styles.statLabel}>Helpful</div>
                                </div>
                                <div style={styles.statItem}>
                                    <div style={styles.statNumber}>{article.category}</div>
                                    <div style={styles.statLabel}>Category</div>
                                </div>
                                <div style={styles.statItem}>
                                    <div style={styles.statNumber}>{article.attachments.length}</div>
                                    <div style={styles.statLabel}>Files</div>
                                </div>
                            </div>

                            <div style={styles.articleFooter}>
                                <div style={styles.actionButtons}>
                                    <button
                                        style={{ ...styles.smallButton, backgroundColor: "#6a0dad", color: "white" }}
                                        onClick={() => handleEditArticle(article)}
                                        title="Edit"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        style={{ ...styles.smallButton, backgroundColor: "#28a745", color: "white" }}
                                        onClick={() => window.open(`/kb/article/${article.id}`, '_blank')}
                                        title="View"
                                    >
                                        <FaEye />
                                    </button>
                                    <button
                                        style={{ ...styles.smallButton, backgroundColor: "#17a2b8", color: "white" }}
                                        onClick={() => handleDuplicateArticle(article)}
                                        title="Duplicate"
                                    >
                                        <FaCopy />
                                    </button>
                                    <button
                                        style={{ ...styles.smallButton, backgroundColor: "#dc3545", color: "white" }}
                                        onClick={() => handleDeleteArticle(article.id)}
                                        title="Delete"
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                                <button
                                    style={{ ...styles.smallButton, backgroundColor: "#f8f9fa", color: "#495057" }}
                                    title="Share"
                                >
                                    <FaShareAlt />
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#6c757d",
                        backgroundColor: "#fff",
                        borderRadius: "10px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                    }}>
                        <FaBook style={{ fontSize: "48px", marginBottom: "20px", opacity: 0.5 }} />
                        <h3 style={{ color: "#2c3e50", marginBottom: "10px", fontSize: "18px" }}>No articles found</h3>
                        <p style={{ fontSize: "14px" }}>Try changing your filters or create a new article</p>
                        <button
                            style={{
                                ...styles.mobileActionButton,
                                marginTop: "20px",
                                padding: "10px 20px"
                            }}
                            onClick={() => setShowNewArticle(true)}
                        >
                            <FaPlus /> Create New Article
                        </button>
                    </div>
                )}
            </div>

            {/* Mobile Menu */}
            {showMobileMenu && (
                <div style={styles.mobileMenu}>
                    <div style={styles.mobileMenuContent}>
                        <div style={styles.mobileMenuHeader}>
                            <h2 style={styles.mobileMenuTitle}>Knowledge Base</h2>
                            <button 
                                style={styles.mobileMenuClose}
                                onClick={() => setShowMobileMenu(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div style={styles.categoryList}>
                            <div
                                style={{
                                    ...styles.categoryItem,
                                    ...(filters.category === "all" ? styles.categoryItemActive : {})
                                }}
                                onClick={() => {
                                    handleFilterChange("category", "all");
                                    setShowMobileMenu(false);
                                }}
                            >
                                <span>All Categories</span>
                                <span style={styles.categoryCount}>{analytics.totalArticles}</span>
                            </div>
                            {categories.map(category => (
                                <div
                                    key={category.id}
                                    style={{
                                        ...styles.categoryItem,
                                        ...(filters.category === category.name ? styles.categoryItemActive : {})
                                    }}
                                    onClick={() => {
                                        handleFilterChange("category", category.name);
                                        setShowMobileMenu(false);
                                    }}
                                >
                                    <span>{category.name}</span>
                                    <span style={styles.categoryCount}>{category.count}</span>
                                </div>
                            ))}
                        </div>
                        
                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Top Searches</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
                                {analytics.searchQueries.map((query, index) => (
                                    <span key={index} style={styles.tag}>
                                        {query}
                                    </span>
                                ))}
                            </div>
                        </div>
                        
                        <div style={{ marginTop: "30px", display: "flex", flexDirection: "column", gap: "10px" }}>
                            <button style={styles.mobileActionButton}>
                                <FaUpload /> Import Articles
                            </button>
                            <button style={{ ...styles.mobileActionButton, backgroundColor: "#fd7e14" }}>
                                <FaDownload /> Export Articles
                            </button>
                            <button style={{ ...styles.mobileActionButton, backgroundColor: "#28a745" }}>
                                <FaChartBar /> View Analytics
                            </button>
                        </div>
                        
                        <div style={{ marginTop: "30px", fontSize: "12px", color: "#6c757d", textAlign: "center" }}>
                            <p>Total Articles: {analytics.totalArticles}</p>
                            <p>Total Views: {analytics.totalViews.toLocaleString()}</p>
                            <p>Helpful Rate: {analytics.helpfulRate}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* New/Edit Article Modal */}
            {showNewArticle && (
                <div style={styles.modalOverlay} onClick={() => setShowNewArticle(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>
                                {editingArticle ? "Edit Article" : "Create New Article"}
                            </h2>
                            <button 
                                style={styles.mobileMenuClose}
                                onClick={() => setShowNewArticle(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Title</label>
                            <input
                                type="text"
                                style={styles.formInput}
                                value={newArticle.title}
                                onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
                                placeholder="Enter article title"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Content</label>
                            <textarea
                                style={styles.formTextarea}
                                value={newArticle.content}
                                onChange={(e) => setNewArticle({ ...newArticle, content: e.target.value })}
                                placeholder="Enter article content (Markdown supported)"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Category</label>
                            <select
                                style={styles.formInput}
                                value={newArticle.category}
                                onChange={(e) => setNewArticle({ ...newArticle, category: e.target.value })}
                            >
                                <option value="">Select category</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.name}>{category.name}</option>
                                ))}
                            </select>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Subcategory</label>
                            <input
                                type="text"
                                style={styles.formInput}
                                value={newArticle.subcategory}
                                onChange={(e) => setNewArticle({ ...newArticle, subcategory: e.target.value })}
                                placeholder="Enter subcategory"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Tags (press Enter to add)</label>
                            <div style={styles.tagInputContainer}>
                                {newArticle.tags.map((tag, index) => (
                                    <span key={index} style={styles.tag}>
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => setNewArticle({
                                                ...newArticle,
                                                tags: newArticle.tags.filter((_, i) => i !== index)
                                            })}
                                            style={{
                                                marginLeft: "5px",
                                                background: "none",
                                                border: "none",
                                                color: "#dc3545",
                                                cursor: "pointer",
                                                fontSize: "12px"
                                            }}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                                <input
                                    type="text"
                                    style={styles.tagInput}
                                    placeholder="Add tags..."
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && e.target.value.trim()) {
                                            setNewArticle({
                                                ...newArticle,
                                                tags: [...newArticle.tags, e.target.value.trim()]
                                            });
                                            e.target.value = '';
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Visibility</label>
                            <select
                                style={styles.formInput}
                                value={newArticle.visibility}
                                onChange={(e) => setNewArticle({ ...newArticle, visibility: e.target.value })}
                            >
                                <option value="public">Public</option>
                                <option value="private">Private</option>
                            </select>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={newArticle.featured}
                                    onChange={(e) => setNewArticle({ ...newArticle, featured: e.target.checked })}
                                    style={{ width: "18px", height: "18px" }}
                                />
                                <span style={styles.formLabel}>Featured Article</span>
                            </label>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                            <button
                                style={{ ...styles.mobileActionButton, backgroundColor: "#6c757d", padding: "10px 15px" }}
                                onClick={() => {
                                    setShowNewArticle(false);
                                    setEditingArticle(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                style={{ ...styles.mobileActionButton, backgroundColor: "#28a745", padding: "10px 15px" }}
                                onClick={handleSaveArticle}
                            >
                                {editingArticle ? "Update" : "Create"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Action Button for Mobile */}
            <button
                style={{
                    position: "fixed",
                    bottom: "20px",
                    right: "20px",
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    backgroundColor: "#6a0dad",
                    color: "white",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(106, 13, 173, 0.3)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    zIndex: 100
                }}
                onClick={() => setShowNewArticle(true)}
            >
                <FaPlus />
            </button>
        </div>
    );
};

export default SuperAdminKnowledgeBaseManager;